// src/servives/api.ts

import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';
const protectedRoutes = ['/admin', '/admin/dashboard', '/admin/settings', '/admin/orders', '/admin/products', '/account'];

// Get the base URL from environment variables
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000/api';

// Axios instance with default configurations
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include credentials such as cookies with requests
});

/**
 * Define a type for the API response.
 */
interface ApiResponse<T = unknown> {
  message?: string;
  data?: T;
}

/**
 * Request Interceptor
 * Adds the Authorization token to the headers if available in localStorage.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        // Ensure headers are initialized
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * Response Interceptor
 * Handles errors such as unauthorized access (401) and network issues.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => response,
  (error: AxiosError<ApiResponse>) => {
    if (error.response) {
      if (error.response.status === 401) {
        // Redirect only if the user is on a protected route
        const currentPath = window.location.pathname;

        // Check if the current path starts with any of the protected routes
        const isProtectedRoute = protectedRoutes.some(route => currentPath.startsWith(route));

        if (isProtectedRoute && typeof window !== 'undefined' && currentPath !== '/customer/login' && currentPath !== '/customer/register') {
          window.location.href = '/customer/login';
        }
      } else {
        // Handle other types of errors (4xx, 5xx)
        console.error(`API Error: ${error.response.status} - ${error.response.data?.message || error.message}`);
      }
    } else {
      // Handle network or other unexpected errors
      console.error(`Network Error: ${error.message}`);
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function for making GET requests.
 * @param url - The endpoint URL.
 * @param params - Optional query parameters.
 * @returns A promise resolving to the response data.
 */
export const get = async <T>(url: string, params: object = {}): Promise<AxiosResponse<ApiResponse<T>>> => {
  try {
    return await apiClient.get<ApiResponse<T>>(url, { params });
  } catch (error) {
    handleError(error);
    throw error;
  }
};

/**
 * Helper function for making POST requests.
 * @param url - The endpoint URL.
 * @param data - The data to send in the request body.
 * @returns A promise resolving to the response data.
 */
export const post = async <T>(url: string, data: object): Promise<AxiosResponse<ApiResponse<T>>> => {
  try {
    return await apiClient.post<ApiResponse<T>>(url, data);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

/**
 * Helper function for making PUT requests.
 * @param url - The endpoint URL.
 * @param data - The data to send in the request body.
 * @returns A promise resolving to the response data.
 */
export const put = async <T>(url: string, data: object): Promise<AxiosResponse<ApiResponse<T>>> => {
  try {
    return await apiClient.put<ApiResponse<T>>(url, data);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

/**
 * Helper function for making DELETE requests.
 * @param url - The endpoint URL.
 * @returns A promise resolving to the response data.
 */
export const del = async <T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> => {
  try {
    return await apiClient.delete<ApiResponse<T>>(url);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

/**
 * Centralized error handling function.
 * @param error - The AxiosError or unknown error to handle.
 */
const handleError = (error: unknown): void => {
  if (error instanceof AxiosError) {
    if (error.response) {
      console.error(`API Error: ${error.response.status} - ${error.response.data?.message || error.message}`);
    } else if (error.request) {
      console.error('No response received from server:', error.message);
    } else {
      console.error('Error setting up request:', error.message);
    }
  } else if (error instanceof Error) {
    console.error('General Error:', error.message);
  } else {
    console.error('Unknown error occurred:', error);
  }
};

export default apiClient;
